import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Checkbox } from '@telus-uds/components-web';

import { FieldWrapper, FieldLabelWrapper } from './css';
import Note from '../../note';
import FieldGuide from './common/fieldGuide';
import FieldTitle from './common/FieldTitle';
import { fieldAdditionalRsiOptions } from '../../utils/constant';

const FieldMultiCheckbox = ({ data }) => {
  const {
    name,
    label,
    options,
    required,
    onChange,
    note,
    readonly,
    value: fieldValue = [],
    formFields,
    setFormFields,
    tooltip,
    tooltipLong,
    toolTipRichText,
    isShowNoteBackground,
    isLabelBold
  } = data;

  const mappedValue = options.reduce((acc, op) => {
    if (_.get(op, 'checked', false)) {
      acc.push(op.fields.value);
    }
    return acc;
  }, []);

  const nonStackableOptions = options.reduce((acc, op) => {
    if (op.checked) {
      _.get(op, 'fields.nonStackableWithOptions', []).forEach(
        nonstackableOp => {
          acc.push(_.get(nonstackableOp, 'fields.value', null));
        }
      );
    }
    return _.compact(acc);
  }, []);

  const allOptions = options.map(op => {
    return {
      ...op,
      disabledOption: nonStackableOptions.includes(op.fields.value)
    };
  });

  if (!_.isEqual(fieldValue, mappedValue)) {
    const updatedField = {
      ...formFields[name],
      value: mappedValue
    };
    setFormFields({ ...formFields, [name]: updatedField });
  }

  const onCheckboxChange = ({ target: { value, name: evtName, checked } }) => {
    const option = allOptions.find(op => op.fields.value === value);
    option.checked = checked;
    let updatedValue;

    if (checked) {
      updatedValue = [...mappedValue, value];
    } else {
      updatedValue = mappedValue.filter(item => item !== value);
    }
    const event = {
      target: { name: evtName, value: updatedValue, checked }
    };
    return onChange && onChange(event);
  };

  if (options.length === 0) return null;
  const tooltipMessage = tooltip || tooltipLong || toolTipRichText;
  return (
    <FieldWrapper isRsiOptions={name === fieldAdditionalRsiOptions}>
      <FieldLabelWrapper>
        <FieldTitle required={required} label={label} isLabelBold={isLabelBold} />
        {tooltipMessage && (
          <FieldGuide tooltip={tooltipMessage} isRichText={toolTipRichText} />
        )}
      </FieldLabelWrapper>
      {note && note.content && <Note type="richText" data={note} isShowNoteBackground={isShowNoteBackground} />}
      <div>
        {allOptions.map(item => {
          const {
            fields: { label: itemLabel, value },
            checked,
            disabledOption
          } = item;
          const id = `${name}${itemLabel}`;
          return (
            <div key={id} style={{ display: 'flex' }}>
              <Checkbox
                label={itemLabel}
                name={name}
                value={value}
                checked={!!checked}
                onChange={() => {
                  // Synthesize the exact same DOM event shape that onCheckboxChange expects:
                  // it destructures { target: { value, name, checked } }
                  onCheckboxChange({ target: { value, name, checked: !checked } });
                }}
                inactive={readonly || disabledOption}
              />
            </div>
          );
        })}
      </div>
    </FieldWrapper>
  );
};

FieldMultiCheckbox.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    required: PropTypes.bool,
    note: PropTypes.shape({
      content: PropTypes.arrayOf(PropTypes.object)
    }),
    readonly: PropTypes.bool,
    value: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string
      })
    ).isRequired,
    formFields: PropTypes.object.isRequired,
    setFormFields: PropTypes.func.isRequired,
    tooltip: PropTypes.string,
    tooltipLong: PropTypes.string,
    toolTipRichText: PropTypes.shape({
      content: PropTypes.arrayOf(PropTypes.object)
    }),
    isShowNoteBackground: PropTypes.bool,
    isLabelBold: PropTypes.bool
  }).isRequired
};

export default FieldMultiCheckbox;
